/**
 * TaskTagsDialog — catalogue de tags colorés (cascade rename sur les tâches).
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tag as TagIcon, Plus, Trash2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#64748b", "#0ea5e9"];

export default function TaskTagsDialog({ open, onOpenChange, onChanged }) {
  const [tags, setTags] = useState([]);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#3b82f6");
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ label: "", color: "" });

  const load = async () => {
    try {
      const r = await api.get("/tasks/tags");
      setTags(r.data.tags || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (!label.trim()) return;
    try {
      await api.post("/tasks/tags", { label: label.trim(), color });
      setLabel("");
      await load(); onChanged?.();
      toast.success("Tag créé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    if (!editId) return;
    try {
      await api.patch(`/tasks/tags/${editId}`, editDraft);
      setEditId(null);
      await load(); onChanged?.();
      toast.success("Tag mis à jour");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce tag du catalogue ? (Les tâches gardent le libellé)")) return;
    try {
      await api.delete(`/tasks/tags/${id}`);
      await load(); onChanged?.();
      toast.success("Tag supprimé");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tags-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><TagIcon className="w-5 h-5" />Catalogue de tags</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Create */}
          <div className="flex gap-2">
            <Input placeholder="Nouveau tag" value={label} onChange={e => setLabel(e.target.value)} data-testid="tag-new-label" />
            <div className="flex gap-1 items-center">
              {DEFAULT_COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-slate-900" : "border-transparent"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`tag-color-${c}`} />
              ))}
            </div>
            <Button onClick={create} disabled={!label.trim()} data-testid="tag-create-btn"><Plus className="w-4 h-4" /></Button>
          </div>

          {/* List */}
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {tags.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucun tag</p>}
            {tags.map(t => (
              <div key={t.id} className="flex items-center gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50" data-testid={`tag-row-${t.id}`}>
                {editId === t.id ? (
                  <>
                    <Input value={editDraft.label} onChange={e => setEditDraft({ ...editDraft, label: e.target.value })} className="h-7 text-sm flex-1" data-testid={`tag-edit-input-${t.id}`} />
                    <div className="flex gap-1">
                      {DEFAULT_COLORS.map(c => (
                        <button key={c} onClick={() => setEditDraft({ ...editDraft, color: c })}
                          className={`w-5 h-5 rounded-full border-2 ${editDraft.color === c ? "border-slate-900" : "border-transparent"}`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" onClick={saveEdit} className="h-7 px-2 text-emerald-600" data-testid={`tag-save-${t.id}`}><Save className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditId(null)} className="h-7 px-2"><X className="w-3 h-3" /></Button>
                  </>
                ) : (
                  <>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="flex-1 text-sm">#{t.label}</span>
                    <Badge variant="outline" className="text-[10px]">{t.usage_count} tâches</Badge>
                    <Button variant="ghost" size="sm" onClick={() => { setEditId(t.id); setEditDraft({ label: t.label, color: t.color }); }} className="h-7 px-2" data-testid={`tag-edit-${t.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(t.id)} className="h-7 px-2 text-red-500" data-testid={`tag-del-${t.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
